/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment';
import { IMAGE_CACHE_DOMAIN } from '../../../constants/CommonConstants';
import {
  NON_STAY_BASED_ANCILLARY_PRODUCT_OFFER,
  NON_STAY_BASED_OFFERS,
  NON_STAY_BASED_PRODUCT_AND_LOYALTY_OFFER,
  PRODUCT_AND_LOYALTY_OFFER,
  STAY_BASED_ALL,
} from '../../../constants/OfferDetailsConstants';
import { MEMBER_EXCLUSIVE, MERCHANDISING_OFFER } from '../../../constants/OfferCardConstants';
import { WIDE_ASPECT_RATIO } from '../../../constants/OffersHeroConstants';
import { getFallbackImg, getNthOccurence } from '../../../utils/CommonUtils';
import { getTomorrowDate } from '../../../utils/OfferUtils';
import config from '../../../lib/config';

const FACET_TYPES = {
  BRANDS: 'BRANDS',
  OFFER_TAGS: 'OFFER_TAGS',
  OFFER_FLAGS: 'OFFER_FLAGS',
  STATES: 'STATES',
  CITIES: 'CITIES',
  COUNTRIES: 'COUNTRIES',
} as const;

export const OFFERS_QUERY_PARAM_PAGE = 'pg';
export const OFFERS_QUERY_PARAM_SORT_BY = 'sortBy';

const OFFERS_SORT_BY_TO_QUERY_VALUE: Record<string, string> = {
  BOOKING_END_DATE: 'expiring-soon',
  BOOKING_START_DATE: 'new',
  OFFER_DISTANCE: 'distance',
};

const OFFERS_QUERY_VALUE_TO_SORT_BY = Object.entries(OFFERS_SORT_BY_TO_QUERY_VALUE).reduce(
  (acc, [sortByCode, queryValue]) => ({
    ...acc,
    [queryValue]: sortByCode,
  }),
  {} as Record<string, string>
);

const getDateFormatted = (date: any): string => {
  return date?.format('YYYY/MM/DD') ?? '';
};

const getFacetTerms = (facets: Record<string, string[]>) => {
  return [
    {
      type: FACET_TYPES.BRANDS,
      dimensions: facets?.['brandFacets'] ?? [],
    },
    {
      type: FACET_TYPES.OFFER_TAGS,
      dimensions: facets?.['offerCategory'] ?? [],
    },
    {
      type: FACET_TYPES.OFFER_FLAGS,
      dimensions: facets?.['offerType'] ?? [],
    },
    {
      type: FACET_TYPES.STATES,
      dimensions: facets?.['filterState'] ?? [],
    },
    {
      type: FACET_TYPES.CITIES,
      dimensions: facets?.['filterCity'] ?? [],
    },
    {
      type: FACET_TYPES.COUNTRIES,
      dimensions: facets?.['filterCountry'] ?? [],
    },
  ];
};

export const resolveOffersSearchMode = (
  deepLinkParams: any,
  options?: {
    useDestinationMetaDataPayload?: boolean; //this flag is used to determine if the destinationMetaData should be used to resolve the search mode when deepLinkParams are not present
    destinationMetaData?: any;
  }
) => {
  const destinationMetaData = options?.destinationMetaData ?? {};
  const useDestinationMetaDataPayload = options?.useDestinationMetaDataPayload === true;

  const hasDeepLinkOverride =
    deepLinkParams?.searchByGeoLoc ||
    deepLinkParams?.searchBystate ||
    deepLinkParams?.searchBycountry ||
    deepLinkParams?.searchByOffersIds ||
    deepLinkParams?.searchByPropertyCodes ||
    deepLinkParams?.searchByStayBasedOfferFlag ||
    deepLinkParams?.isEmptyOfferIds ||
    deepLinkParams?.isEmptyPropertyCodes ||
    deepLinkParams?.isEmptyStayBasedOfferFlag;

  const metaDrivenGeo =
    useDestinationMetaDataPayload &&
    !hasDeepLinkOverride &&
    !destinationMetaData?.isCountryEnabled &&
    !destinationMetaData?.isStateEnabled;
  const metaDrivenState = useDestinationMetaDataPayload && !hasDeepLinkOverride && destinationMetaData?.isStateEnabled;
  const metaDrivenCountry =
    useDestinationMetaDataPayload &&
    !hasDeepLinkOverride &&
    destinationMetaData?.isCountryEnabled &&
    !destinationMetaData?.isStateEnabled;

  return {
    ...deepLinkParams,
    searchByGeoLoc: !!deepLinkParams?.searchByGeoLoc || metaDrivenGeo,
    searchBystate: !!deepLinkParams?.searchBystate || metaDrivenState,
    searchBycountry: !!deepLinkParams?.searchBycountry || metaDrivenCountry,
  };
};

export const getOffersSearchQueryName = (mode: any) => {
  if (mode?.searchByGeoLoc) {
    return 'phoenixOffersSearchByGeolocation';
  }
  if (mode?.searchBystate || mode?.searchBycountry) {
    return 'phoenixOffersSearchByArea';
  }
  return 'phoenixOffersListSearch';
};

export const buildOffersSearchVariables = ({
  acceptLanguage,
  urlParams,
  ariesSearchAddress,
  mode,
  limit,
  offset,
  sortBy,
  facets,
  hiddenCountry,
  destinationMetaData,
}: {
  acceptLanguage?: string;
  urlParams: URLSearchParams;
  ariesSearchAddress?: any;
  mode: any;
  limit: number;
  offset: number;
  sortBy: string;
  facets: Record<string, string[]>;
  hiddenCountry?: string;
  destinationMetaData?: any;
}) => {
  const queryFromDate =
    acceptLanguage === 'en-GB'
      ? moment(urlParams?.get('fromDate'), 'DD/MM/YYYY').format('MM/DD/YYYY')
      : urlParams?.get('fromDate');
  const stayStartDate = getDateFormatted(moment(urlParams?.has('fromDate') ? queryFromDate : moment(new Date())));
  const queryToDate =
    acceptLanguage === 'en-GB'
      ? moment(urlParams?.get('toDate'), 'DD/MM/YYYY').format('MM/DD/YYYY')
      : urlParams?.get('toDate');
  const stayEndDate = urlParams?.has('toDate') ? getDateFormatted(moment(queryToDate)) : getTomorrowDate(true);

  const variables: any = {
    input: {
      queries: [],
      facets: {
        terms: getFacetTerms(facets),
      },
    },
    limit,
    offset,
    sort: {
      field: sortBy,
    },
  };

  if (mode?.searchByGeoLoc) {
    const latValue =
      ariesSearchAddress?.latitude ??
      destinationMetaData?.latitude ??
      urlParams?.get('destinationAddress.latitude') ??
      '';
    const lngValue =
      ariesSearchAddress?.longitude ??
      destinationMetaData?.longitude ??
      urlParams?.get('destinationAddress.longitude') ??
      '';
    variables.input.queries = [
      { id: 'LATITUDE', values: String(latValue) },
      { id: 'LONGITUDE', values: String(lngValue) },
      { id: 'STAY_START_DATE', values: stayStartDate },
      { id: 'STAY_END_DATE', values: stayEndDate || null },
    ];
  } else if (mode?.searchBystate || mode?.searchBycountry) {
    const countryValue =
      hiddenCountry ||
      (Array.isArray(ariesSearchAddress?.country) ? ariesSearchAddress?.country?.[0] : ariesSearchAddress?.country) ||
      destinationMetaData?.countryCode ||
      urlParams?.get('destinationAddress.country') ||
      '';
    const stateValue =
      (Array.isArray(ariesSearchAddress?.stateProvince)
        ? ariesSearchAddress?.stateProvince?.[0]
        : ariesSearchAddress?.stateProvince) ||
      destinationMetaData?.stateCode ||
      urlParams?.get('destinationAddress.stateProvince') ||
      '';
    variables.input.queries = [
      { id: 'STAY_START_DATE', values: stayStartDate },
      { id: 'STAY_END_DATE', values: stayEndDate || null },
      { id: 'COUNTRY', values: countryValue },
      ...(mode?.searchBystate ? [{ id: 'STATE', values: stateValue }] : []),
    ];
  }

  if (mode?.searchByOffersIds) {
    variables.input.queries = [
      ...(variables.input?.queries ?? []),
      {
        id: 'OFFERS',
        values: mode?.offerIds ?? [],
      },
    ];
  }

  if (mode?.isEmptyOfferIds || mode?.isEmptyPropertyCodes || mode?.isEmptyStayBasedOfferFlag) {
    variables.input.queries = [
      ...(variables.input?.queries ?? []),
      {
        id: 'STAY_START_DATE',
        values: stayStartDate,
      },
      {
        id: 'STAY_END_DATE',
        values: stayEndDate || null,
      },
    ];
    variables.input.offertype = [NON_STAY_BASED_ANCILLARY_PRODUCT_OFFER, STAY_BASED_ALL];
  }

  if (mode?.searchByPropertyCodes) {
    variables.input.queries = [
      ...(variables.input?.queries ?? []),
      {
        id: 'PROPERTIES',
        values: mode?.propertyCodes ?? [],
      },
    ];
    variables.input.offertype = [
      NON_STAY_BASED_PRODUCT_AND_LOYALTY_OFFER,
      NON_STAY_BASED_ANCILLARY_PRODUCT_OFFER,
      STAY_BASED_ALL,
    ];
  }

  if (mode?.searchByStayBasedOfferFlag) {
    variables.input.queries = [
      ...(variables.input?.queries ?? []),
      {
        id: 'STAY_START_DATE',
        values: stayStartDate,
      },
      {
        id: 'STAY_END_DATE',
        values: stayEndDate || null,
      },
    ];
    variables.input.offertype = [NON_STAY_BASED_PRODUCT_AND_LOYALTY_OFFER, NON_STAY_BASED_ANCILLARY_PRODUCT_OFFER];
  }

  if (
    !mode?.searchByOffersIds &&
    !mode?.searchByPropertyCodes &&
    !mode?.searchByStayBasedOfferFlag &&
    !mode?.isEmptyOfferIds &&
    !mode?.isEmptyPropertyCodes &&
    !mode?.isEmptyStayBasedOfferFlag &&
    !mode?.searchByGeoLoc &&
    !mode?.searchBystate &&
    !mode?.searchBycountry
  ) {
    variables.input.queries = [
      ...(variables.input?.queries ?? []),
      {
        id: 'STAY_START_DATE',
        values: stayStartDate,
      },
      {
        id: 'STAY_END_DATE',
        values: stayEndDate || null,
      },
    ];
    variables.input.offertype = [NON_STAY_BASED_ANCILLARY_PRODUCT_OFFER, STAY_BASED_ALL];
  }

  return variables;
};

const isMemberExclusive = (memberLevel: string[] = []): boolean => {
  return memberLevel?.find(ele => MEMBER_EXCLUSIVE.includes(ele)) !== undefined;
};

const getFacetBuckets = (facets: any[] = [], enumCode: string) => {
  return facets?.find((el: { type?: { enumCode?: string } }) => el?.type?.enumCode === enumCode)?.buckets ?? [];
};

export const mapOffersSearchResponse = ({
  uxlData,
  model,
  imageDomain,
  subdirectory,
}: {
  uxlData: any;
  model: any;
  imageDomain?: string;
  subdirectory?: string;
}) => {
  const resolvedImageDomain = imageDomain ?? IMAGE_CACHE_DOMAIN;
  const isChinaDomain = resolvedImageDomain === 'https://cache.marriott.com.cn';
  const defaultImage = getFallbackImg(
    isChinaDomain ? config.FALLBACK_CN_IMG_URI : config.FALLBACK_IMG_URI,
    WIDE_ASPECT_RATIO
  );

  const marketingOffersSearch = uxlData?.marketing?.offers?.search ?? uxlData?.offersSearch ?? {};
  const offersResultsData = marketingOffersSearch?.edges ?? [];
  const offerFacetsBucket = marketingOffersSearch?.facets ?? [];
  const availableFilterCity = getFacetBuckets(offerFacetsBucket, FACET_TYPES.CITIES);

  const cityInData =
    offersResultsData?.[0]?.node?.associatedPropertiesAndRates?.propertiesAndRates?.[0]?.property?.contactInformation
      ?.address?.city ??
    offersResultsData?.[0]?.node?.participatingProperties?.properties?.[0]?.contactInformation?.address?.city ??
    '';

  const cardCount = marketingOffersSearch?.total ?? 0;
  const offerCards = offersResultsData?.map((item: any, index: number) => {
    const offerId = item?.node?.id?.toLowerCase();
    const header = item?.node?.title;
    const description = item?.node?.descriptionTeaser;
    const offerUrl = item?.node?.url;
    const numProperties = item?.node?.numProperties ?? 0;
    const url = offerUrl?.substring(getNthOccurence(offerUrl, '/', 3), offerUrl?.length);
    const seoNickname = item?.node?.seoNickname;
    const urlTitle = item?.node?.urlTitle;
    const propertyDetails = item?.node?.associatedPropertiesAndRates?.propertiesAndRates?.[0]?.property;
    const marshaCode = propertyDetails?.id?.toLowerCase();
    const prefixedSubdirectory = subdirectory ? `/${subdirectory.replace(/^\/+/, '')}` : '';
    const spoUrl = `${prefixedSubdirectory}/offers/${urlTitle}-${offerId}/${marshaCode}-${seoNickname}`;
    const offerCta = numProperties === 1 ? spoUrl : url;
    const parentOfferType = item?.node?.parentOfferType;
    const offerType = item?.node?.offerType;
    const callToActionLabel = item?.node?.callToActionLabel;
    const callToActionLink = item?.node?.callToActionLink;
    const ctaLinkText = parentOfferType === NON_STAY_BASED_OFFERS ? callToActionLabel : model?.offersDetails;
    const ctaLink =
      offerType === PRODUCT_AND_LOYALTY_OFFER || parentOfferType === MERCHANDISING_OFFER ? callToActionLink : offerCta;

    const imageUrls = item?.node?.media?.primaryImage?.imageUrls;
    const primaryImageSrc = item?.node?.media?.primaryImage?.imageSrc;
    const isValidCnImg = primaryImageSrc?.includes('zh_cn');
    const imagePath = isChinaDomain
      ? isValidCnImg
        ? imageUrls?.wideHorizontal
        : defaultImage
      : imageUrls?.wideHorizontal;
    const imageNodePrimary = imagePath;
    const imageMapping =
      imageNodePrimary && imageNodePrimary !== null && imageNodePrimary !== ''
        ? resolvedImageDomain + imageNodePrimary
        : resolvedImageDomain + defaultImage;
    const image = cardCount && cardCount > 0 ? imageMapping : defaultImage;

    const cardLocDetails = model?.trackingProperties?.location
      ? `${model.trackingProperties.location}-card-${index}`
      : `card-${index}`;
    const merchandCat = model?.trackingProperties?.merchandisingCategory
      ? model.trackingProperties.merchandisingCategory
      : 'nonCobrand';

    const propertyName = propertyDetails?.basicInformation?.name;
    const locationVal = numProperties > 0 ? (numProperties > 1 ? model?.offersMultipleLocation : propertyName) : '';
    const showCityIcon = locationVal !== '' && locationVal !== undefined && numProperties > 0;
    const memberLevel = item?.node?.memberLevel;
    const memberExclusive = isMemberExclusive(memberLevel) ?? false;
    const badgeText = memberExclusive ? model?.offersMemberexclusive : '';

    return {
      cardId: offerId,
      cardProps: {
        header: header,
        headerTag: 'h2',
        description: description,
        cardVerticalVariations: 'Tall16:9',
        dynamicMedia: {
          altText: header,
          assetPath: image,
          dynamic: true,
          renditions: [
            {
              renditionPath: `${image}?downsize=364px:*`,
              mediaValue: '768px',
              width: 0,
              dynamic: true,
              damPath:
                '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
              mediaQuery: 'min-width',
              height: 0,
            },
            {
              renditionPath: `${image}?downsize=344px:*`,
              mediaValue: '576px',
              width: 0,
              dynamic: true,
              damPath:
                '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
              mediaQuery: 'min-width',
              height: 0,
            },
            {
              renditionPath: `${image}?downsize=575px:*`,
              mediaValue: '576px',
              width: 0,
              dynamic: true,
              damPath:
                '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
              mediaQuery: 'max-width',
              height: 0,
            },
          ],
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
        },
        ctaType: 'primaryButton',
        ':type': 'mi-aem-common-spa/components/content/cardvertical',
        badgeIcon: 'icon-lock',
        badgeText: badgeText,
        imgAltText: header,
        fileReferenceImage:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
        ctaLinkText: ctaLinkText,
        ctaLink: ctaLink,
        trackingProperties: {
          atCCeVar48: model?.trackingProperties?.atCCeVar48 ?? null,
          trackingDescription: header,
          clickTrack: model?.trackingProperties?.clickTrack,
          impressionTrack: model?.trackingProperties?.impressionTrack,
          location: model?.trackingProperties?.location,
          trackingContentPosition: cardLocDetails,
          merchandisingCategory: merchandCat,
          impressionCount: model?.trackingProperties?.impressionCount,
          trackingTag: model?.trackingProperties?.trackingTag,
          trackingOfferType: model?.trackingProperties?.trackingOfferType ?? null,
          cardLocation: model?.trackingProperties?.cardLocation,
        },
        iconFontValue: showCityIcon ? 'icon-city' : '',
        iconText: locationVal,
        styleclass: model?.cardTheme,
        openInNewTab: false,
      },
    };
  });

  return {
    cardCount,
    offerCards,
    cityInData,
    facetsData: {
      availableBrands: getFacetBuckets(offerFacetsBucket, FACET_TYPES.BRANDS),
      availableOfferCategory: getFacetBuckets(offerFacetsBucket, FACET_TYPES.OFFER_TAGS),
      availableOfferType: getFacetBuckets(offerFacetsBucket, FACET_TYPES.OFFER_FLAGS),
      availableFilterState: getFacetBuckets(offerFacetsBucket, FACET_TYPES.STATES),
      availableFilterCity,
      availableFilterCountry: getFacetBuckets(offerFacetsBucket, FACET_TYPES.COUNTRIES),
    },
  };
};

export const getDefaultOffersSortBy = (mode: any) => {
  return mode?.searchByGeoLoc ? 'OFFER_DISTANCE' : 'BOOKING_END_DATE';
};

const getAllowedSortByOptions = (mode: any) => {
  const baseSortByOptions = ['BOOKING_END_DATE', 'BOOKING_START_DATE'];
  return mode?.searchByGeoLoc ? ['OFFER_DISTANCE', ...baseSortByOptions] : baseSortByOptions;
};

export const getOffersSortByFromRequest = (reqUrl?: string, host?: string) => {
  try {
    if (!reqUrl || !host) return '';
    const url = new URL(reqUrl, `http://${host}`);
    const sortByQueryValue = (url.searchParams.get(OFFERS_QUERY_PARAM_SORT_BY) ?? '').trim();
    if (sortByQueryValue === '') {
      return '';
    }

    // Backward compatibility: accept legacy enum values if present in URL.
    if (Object.prototype.hasOwnProperty.call(OFFERS_SORT_BY_TO_QUERY_VALUE, sortByQueryValue)) {
      return sortByQueryValue;
    }

    return OFFERS_QUERY_VALUE_TO_SORT_BY[sortByQueryValue] ?? '';
  } catch (error) {
    return '';
  }
};

export const getOffersSortByQueryFromRequest = (reqUrl?: string, host?: string) => {
  try {
    if (!reqUrl || !host) return '';
    const url = new URL(reqUrl, `http://${host}`);
    const sortByQueryValue = (url.searchParams.get(OFFERS_QUERY_PARAM_SORT_BY) ?? '').trim();
    if (sortByQueryValue === '') {
      return '';
    }

    if (OFFERS_QUERY_VALUE_TO_SORT_BY[sortByQueryValue]) {
      return sortByQueryValue;
    }

    // Backward compatibility: normalize legacy enum values to SEO-friendly query values.
    return OFFERS_SORT_BY_TO_QUERY_VALUE[sortByQueryValue] ?? '';
  } catch (error) {
    return '';
  }
};

export const getOffersSortByQueryValue = (sortBy?: string) => {
  if (!sortBy) return '';
  return OFFERS_SORT_BY_TO_QUERY_VALUE[sortBy] ?? '';
};

export const resolveOffersSortBy = ({
  requestedSortBy,
  mode,
  fallbackSortBy,
}: {
  requestedSortBy?: string;
  mode: any;
  fallbackSortBy: string;
}) => {
  const allowedSortByOptions = getAllowedSortByOptions(mode);
  return requestedSortBy && allowedSortByOptions.includes(requestedSortBy) ? requestedSortBy : fallbackSortBy;
};

export const getSeoFriendlyOffersUrl = ({
  baseUrl,
  page,
  sortBy,
}: {
  baseUrl: string;
  page?: number;
  sortBy?: string;
}) => {
  const resolvedUrl = new URL(baseUrl, 'http://localhost');

  if (typeof page === 'number') {
    if (page <= 1) {
      resolvedUrl.searchParams.delete(OFFERS_QUERY_PARAM_PAGE);
    } else {
      resolvedUrl.searchParams.set(OFFERS_QUERY_PARAM_PAGE, String(page));
    }
  }

  if (typeof sortBy === 'string' && sortBy !== '') {
    const sortByQueryValue = getOffersSortByQueryValue(sortBy);
    if (sortByQueryValue !== '') {
      resolvedUrl.searchParams.set(OFFERS_QUERY_PARAM_SORT_BY, sortByQueryValue);
    } else {
      resolvedUrl.searchParams.delete(OFFERS_QUERY_PARAM_SORT_BY);
    }
  }

  return `${resolvedUrl.pathname}${resolvedUrl.search}`;
};

export const getOffersPageNo = (reqUrl?: string, host?: string) => {
  try {
    if (!reqUrl || !host) return 1;
    const url = new URL(reqUrl, `http://${host}`);
    const pageNo = Number(url.searchParams.get(OFFERS_QUERY_PARAM_PAGE) ?? '1');
    return Number.isFinite(pageNo) && pageNo > 0 ? pageNo : 1;
  } catch (error) {
    return 1;
  }
};

export const getOffersPaginationModel = (pageNo: number, cardsPerPage: number, totalCount: number) => {
  return {
    currentPage: pageNo,
    cardsPerPage,
    totalCount,
    queryParamName: OFFERS_QUERY_PARAM_PAGE,
  };
};

export const getFlexibleDateRange = (searchFormModel?: any) => {
  const flexibleDefaultLengthOfStay = Number(searchFormModel?.flexibleDefaultLengthOfStay ?? 1);
  const futureDateOffset = Number(searchFormModel?.futureDateOffset ?? 30);
  const fromMoment = moment().utc().add(futureDateOffset, 'days').startOf('day');
  const toMoment = moment()
    .utc()
    .add(futureDateOffset + flexibleDefaultLengthOfStay, 'days')
    .startOf('day');

  return {
    flexibleDefaultLengthOfStay,
    fromDateIso: fromMoment.toISOString(),
    toDateIso: toMoment.toISOString(),
    fromDateQuery: fromMoment.format('MM/DD/YYYY'),
    toDateQuery: toMoment.format('MM/DD/YYYY'),
  };
};
